import React from 'react';
import { X, Download, ExternalLink } from 'lucide-react';

const FilePreviewModal = ({ file, onClose }) => {
    
    // Helper to construct the URL
    const getUrl = (path, forceInline = false) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        
        // Clean the path
        let cleanPath = path;
        if (path.startsWith('/api/files/')) {
            cleanPath = path; // Already correct base
        } else {
            // Strip leading slashes and rebuild
            cleanPath = `/api/files/${path.replace(/^\/+/, '').split('/').pop()}`;
        }

        // APPEND INLINE FLAG: This prevents auto-download
        if (forceInline) {
            return `${cleanPath}?inline=true`;
        }
        return cleanPath;
    };

    // 1. View URL (For the Iframe/Image) - Has ?inline=true
    const viewUrl = getUrl(file.path, true);
    
    // 2. Download URL (For the button) - No flag (triggers download)
    const downloadUrl = getUrl(file.path, false);

    const isImage = file.type?.startsWith('image/') || file.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    const isPdf = file.type === 'application/pdf' || file.name?.match(/\.pdf$/i);

    return (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-90 flex items-center justify-center p-4">
            <div className="relative w-full max-w-6xl h-full max-h-[90vh] flex flex-col bg-transparent">
                
                {/* Toolbar */}
                <div className="absolute top-0 right-0 p-4 flex gap-2 z-50">
                    {/* Open in New Tab (Fallback for mobile) */}
                    <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="bg-white text-black p-2 rounded-full hover:bg-gray-200 shadow-lg" title="Open in New Tab">
                        <ExternalLink size={20}/>
                    </a>
                    {/* Download Button */}
                    <a href={downloadUrl} download className="bg-white text-black p-2 rounded-full hover:bg-gray-200 shadow-lg" title="Download">
                        <Download size={20}/>
                    </a>
                    {/* Close Button */}
                    <button onClick={onClose} className="bg-white text-black p-2 rounded-full hover:bg-gray-200 shadow-lg">
                        <X size={20}/>
                    </button>
                </div>
                
                {/* Content Area */}
                <div className="flex-1 flex items-center justify-center overflow-hidden bg-gray-900 rounded-lg">
                    {isImage ? (
                        <img 
                            src={viewUrl} 
                            alt="preview" 
                            className="max-w-full max-h-full object-contain" 
                        />
                    ) : isPdf ? (
                        // IFRAME is the most reliable way to trigger the browser's PDF viewer
                        <iframe 
                            src={viewUrl} 
                            className="w-full h-full bg-white" 
                            title="PDF Preview"
                            frameBorder="0"
                        />
                    ) : (
                        <div className="bg-white p-10 rounded-lg text-center shadow-2xl">
                            <p className="mb-4 text-lg font-bold text-gray-800">Cannot preview this file type.</p>
                            <p className="text-sm text-gray-500 mb-6">{file.name}</p>
                            <a href={downloadUrl} download className="px-6 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700">
                                Download File
                            </a>
                        </div>
                    )}
                </div>
                
                {/* Footer Info */}
                <div className="mt-4 text-white text-center select-text">
                    <p className="font-bold text-lg">{file.name}</p>
                </div>
            </div>
        </div>
    );
};

export default FilePreviewModal;